import React from 'react';
import { Loader2, Calculator, Building2, Sparkles } from 'lucide-react';

/**
 * Enhanced Loading Components with animations
 */

// Skeleton loader for form fields
export const SkeletonField = ({ className = "" }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="skeleton h-4 w-24 mb-2 rounded"></div>
    <div className="skeleton h-10 w-full rounded-md"></div>
  </div>
);

// Skeleton loader for cards
export const SkeletonCard = ({ className = "" }) => (
  <div className={`animate-pulse border rounded-lg p-6 ${className}`}>
    <div className="skeleton h-6 w-32 mb-4 rounded"></div>
    <div className="space-y-3">
      <div className="skeleton h-4 w-full rounded"></div>
      <div className="skeleton h-4 w-3/4 rounded"></div>
      <div className="skeleton h-4 w-1/2 rounded"></div>
    </div>
  </div>
);

// Loading spinner with brand colors
export const LoadingSpinner = ({ size = "md", className = "" }) => {
  const sizes = {
    sm: "w-4 h-4",
    md: "w-6 h-6", 
    lg: "w-8 h-8",
    xl: "w-12 h-12"
  };

  return (
    <Loader2 className={`animate-spin text-brand-gold ${sizes[size]} ${className}`} />
  );
};

// Enhanced loading button
export const LoadingButton = ({ 
  loading, 
  children, 
  onClick, 
  disabled, 
  variant = "default",
  className = "",
  ...props 
}) => {
  const baseClasses = "relative overflow-hidden transition-all duration-200 disabled:opacity-50";
  const variants = {
    default: "btn-gold-hover",
    outline: "btn-outline-hover",
    ghost: "hover:bg-brand-light-grey text-brand-black"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variants[variant]} ${className}`}
      {...props}
    >
      <span className={`flex items-center justify-center gap-2 ${loading ? 'opacity-0' : 'opacity-100'}`}>
        {children}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" />
        </span>
      )}
    </button>
  );
};

// Calculation loading state with progress
export const CalculationLoader = ({ stage = "preparing", progress = 0 }) => {
  const stages = {
    preparing: { icon: Calculator, text: "Preparing your calculation...", color: "text-brand-gold" },
    analyzing: { icon: Building2, text: "The Captif AI agent is calculating your budget...", color: "text-brand-gold" },
    calculating: { icon: Building2, text: "The Captif AI agent is calculating your budget...", color: "text-brand-gold" },
    finalizing: { icon: Building2, text: "Almost there, finalizing your personalized estimate...", color: "text-brand-gold" }
  };

  const currentStage = stages[stage] || stages.preparing;
  const Icon = currentStage.icon;

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6 animate-fade-in">
      {/* Static icon - removed bouncing animation */}
      <div className="relative">
        <Icon className={`w-12 h-12 ${currentStage.color}`} />
      </div>

      {/* Progress text */}
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-brand-black">
          {currentStage.text}
        </h3>
        <p className="text-sm text-brand-grey">
          This should only take a moment...
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-64 max-w-full">
        <div className="bg-brand-light-grey rounded-full h-2 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-brand-gold to-yellow-400 transition-all duration-500 ease-out"
            style={{ 
              width: `${Math.max(progress, 10)}%`,
              transform: progress < 100 ? 'translateX(-100%)' : 'translateX(0%)',
              animation: progress < 100 ? 'shimmer 2s infinite' : 'none'
            }}
          />
        </div>
        <div className="mt-2 text-xs text-center text-brand-grey">
          {Math.round(progress)}% complete
        </div>
      </div>

      {/* Rotating dots */}
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 bg-brand-gold rounded-full animate-bounce"
            style={{ 
              animationDelay: `${i * 0.2}s`,
              animationDuration: '1s'
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Form step progress indicator
export const StepProgress = ({ currentStep, totalSteps, steps = [] }) => {
  return (
    <div className="w-full py-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-brand-black">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="text-sm text-brand-grey">
          {Math.round((currentStep / totalSteps) * 100)}% Complete
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="w-full bg-brand-light-grey rounded-full h-2 mb-4">
        <div 
          className="bg-brand-gold h-2 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step indicators */}
      {steps.length > 0 && (
        <div className="flex justify-between">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`flex flex-col items-center text-xs ${
                index < currentStep 
                  ? 'text-brand-gold' 
                  : index === currentStep 
                  ? 'text-brand-black' 
                  : 'text-brand-grey'
              }`}
            >
              <div 
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mb-1 transition-all duration-300 ${
                  index < currentStep
                    ? 'bg-brand-gold border-brand-gold text-white'
                    : index === currentStep
                    ? 'border-brand-gold text-brand-gold animate-pulse'
                    : 'border-gray-300 text-gray-400'
                }`}
              >
                {index < currentStep ? '✓' : index + 1}
              </div>
              <span className="text-center max-w-16">
                {step.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Error state component
export const ErrorState = ({ 
  title = "Something went wrong", 
  message = "Please try again or contact support.",
  onRetry,
  className = "" 
}) => (
  <div className={`text-center p-8 space-y-4 animate-fade-in ${className}`}>
    <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
      <span className="text-2xl">⚠️</span>
    </div>
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{message}</p>
    </div>
    {onRetry && (
      <LoadingButton
        onClick={onRetry}
        variant="outline"
        className="mt-4"
      >
        Try Again
      </LoadingButton>
    )}
  </div>
);

// Success state component
export const SuccessState = ({ 
  title = "Success!", 
  message,
  onContinue,
  className = "" 
}) => (
  <div className={`text-center p-8 space-y-4 animate-fade-in ${className}`}>
    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
      <span className="text-2xl">✅</span>
    </div>
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {message && <p className="text-gray-600">{message}</p>}
    </div>
    {onContinue && (
      <LoadingButton
        onClick={onContinue}
        className="mt-4"
      >
        Continue
      </LoadingButton>
    )}
  </div>
);

// Floating action notification
export const FloatingNotification = ({ 
  type = "info", 
  message, 
  onClose, 
  duration = 5000,
  className = "" 
}) => {
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 300); // Allow fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const types = {
    success: { 
      bg: "bg-white", 
      border: "border-brand-gold",
      text: "text-brand-black",
      icon: "✓",
      iconBg: "bg-brand-gold",
      iconText: "text-white"
    },
    error: { 
      bg: "bg-white", 
      border: "border-red-500",
      text: "text-brand-black",
      icon: "⚠",
      iconBg: "bg-red-500",
      iconText: "text-white"
    },
    warning: { 
      bg: "bg-white", 
      border: "border-brand-gold",
      text: "text-brand-black",
      icon: "⚠",
      iconBg: "bg-brand-gold",
      iconText: "text-brand-black"
    },
    info: { 
      bg: "bg-white", 
      border: "border-brand-grey",
      text: "text-brand-black",
      icon: "ℹ",
      iconBg: "bg-brand-grey",
      iconText: "text-white"
    }
  };

  const config = types[type] || types.info;

  return (
    <div className={`
      fixed top-4 right-4 z-50 transform transition-all duration-300 
      ${visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}
      ${className}
    `}>
      <div className={`
        ${config.bg} ${config.text} border-2 ${config.border} 
        px-4 py-3 rounded-lg shadow-xl 
        flex items-center space-x-3 max-w-sm
      `}>
        <span className={`${config.iconBg} ${config.iconText} w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold`}>
          {config.icon}
        </span>
        <span className="flex-1 font-medium">{message}</span>
        <button 
          onClick={() => setVisible(false)}
          className={`${config.text} hover:text-brand-gold ml-2 text-xl font-bold`}
        >
          ×
        </button>
      </div>
    </div>
  );
};